// SENTINEL: 8_Fitness - Rich Text Editor (Clean Code, Formatting, Dark Mode)
// Vitality: Healthy (Pass 10 Cleanliness Upgrade)
import React, { useEffect, useRef, useState } from 'react';
import { Bold, Italic, List, ListOrdered, Type, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Highlighter } from 'lucide-react';
import { logger } from '../services/loggingService';

interface RichTextEditorProps {
    initialContent: string;
    onChange: (html: string) => void;
    theme: 'light' | 'dark';
    placeholder?: string;
    hideHeadings?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ initialContent, onChange, theme, placeholder, hideHeadings = false }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    // SENTINEL: Tool Efficacy Tracking
    const lastActionTimeRef = useRef<number>(0);
    const lastActionToolRef = useRef<string>('');

    // Smart Overlay State
    const [hoveredBlock, setHoveredBlock] = useState<{ rect: DOMRect; text: string } | null>(null);
    const [copyFeedback, setCopyFeedback] = useState(false);

    // Initialize content
    useEffect(() => {
        if (contentRef.current && contentRef.current.innerHTML !== initialContent) {
            contentRef.current.innerHTML = initialContent;
        }
    }, [initialContent]);

    // SENTINEL: Enforce clean code blocks (no spellcheck)
    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (contentRef.current) {
                const pres = contentRef.current.querySelectorAll('pre');
                pres.forEach(pre => {
                    if (pre.getAttribute('spellcheck') !== 'false') {
                        pre.setAttribute('spellcheck', 'false');
                        pre.setAttribute('autocorrect', 'off');
                        pre.setAttribute('autocapitalize', 'off');
                    }
                });
            }
        });

        if (contentRef.current) {
            observer.observe(contentRef.current, { childList: true, subtree: true });
        }
        return () => observer.disconnect();
    }, []);

    const handleInput = () => {
        if (contentRef.current) {
            onChange(contentRef.current.innerHTML);
        }
    };

    const exec = (command: string, value: string | undefined = undefined) => {
        document.execCommand(command, false, value);
        if (contentRef.current) contentRef.current.focus();

        // SENTINEL: Track formatting intent
        if (command !== 'fontSize' && command !== 'foreColor') {
            logger.info('Canvas', 'Text Format Applied', { tool: command, value });

            // Record action for simple undo efficacy tracking
            lastActionTimeRef.current = Date.now();
            lastActionToolRef.current = command;
        }
    };

    const ToolbarButton = ({ icon: Icon, command, value, label, active = false }: any) => (
        <button
            onMouseDown={(e) => { e.preventDefault(); exec(command, value); }}
            className={`p-1.5 rounded transition-colors ${active
                ? 'bg-cc-accent-muted text-cc-accent'
                : 'hover:bg-cc-tile-hover text-cc-text-muted'
                }`}
            title={label}
        >
            <Icon size={16} />
        </button>
    );

    const cleanContent = (html: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Aggressive Cleanup: Remove non-semantic wrappers
        const unwrap = (el: Element) => {
            const parent = el.parentNode;
            while (el.firstChild) parent?.insertBefore(el.firstChild, el);
            parent?.removeChild(el);
        };

        // 1. Convert Text Separators to HR
        // Walk text nodes to find "----" patterns
        const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
        let currentNode = walker.nextNode();
        while (currentNode) {
            const val = currentNode.nodeValue || '';
            // Detect 3 or more dashes/underscores as a separator
            if (val.trim().match(/^[-_]{3,}$/)) {
                const hr = doc.createElement('hr');
                currentNode.parentNode?.replaceChild(hr, currentNode);
            }
            currentNode = walker.nextNode();
        }

        // 2. Strip Attributes & Unwrap formatting wrappers
        doc.querySelectorAll('*').forEach(el => {
            const tagName = el.tagName.toLowerCase();

            // CRITICAL FIX: Do not attempt to unwrap or remove the document structure itself.
            // This prevents "Only one element on document allowed" errors and infinite loops.
            if (['html', 'head', 'body'].includes(tagName)) return;

            // Script/Iframe/Style tags: KILL
            if (['script', 'iframe', 'style', 'meta', 'link'].includes(tagName)) {
                el.remove();
                return;
            }

            // Semantic Tags: Keep but Clean Attributes
            if (['b', 'strong', 'i', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'li', 'br', 'hr', 'blockquote', 'code', 'pre', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'a'].includes(tagName)) {
                // Remove all attributes except specific safe ones
                const attrs = Array.from(el.attributes);
                attrs.forEach(attr => {
                    const name = attr.name.toLowerCase();
                    if (tagName === 'a' && name === 'href') return; // Keep links
                    if (tagName === 'img' && (name === 'src' || name === 'alt')) return; // Keep images
                    el.removeAttribute(name);
                });

                if (tagName === 'table') {
                    (el as HTMLElement).style.width = '100%';
                    (el as HTMLElement).style.borderCollapse = 'collapse';
                }
            }
            // Wrapper Tags (Span, Font, Div): UNWRAP if they are just formatting containers
            // We unwrap DIVs too because they often mess up spacing in our RTE if nested deeply
            else if (['span', 'font', 'div'].includes(tagName)) {
                unwrap(el);
            }
            // Unknown tags: Unwrap them to preserve text content
            else {
                unwrap(el);
            }
        });

        // 3. Smart Formatting (Optional: Convert "Key: Value" to bold?)
        // Let's keep it simple for now to ensure cleanliness first.

        return doc.body.innerHTML;
    };

    // Sanitize initial content on mount
    useEffect(() => {
        if (contentRef.current && initialContent) {
            // If content looks "dirty" (inline styles), clean it. 
            // Logic: If it has style= or class=, it's likely from external paste or legacy.
            if (initialContent.includes('style=') || initialContent.includes('class=')) {
                const cleaned = cleanContent(initialContent);
                if (cleaned !== initialContent) {
                    contentRef.current.innerHTML = cleaned;
                } else {
                    contentRef.current.innerHTML = initialContent;
                }
            } else {
                contentRef.current.innerHTML = initialContent;
            }
        }
    }, []);

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const html = e.clipboardData.getData('text/html');

        // Always prefer HTML if available to preserve semantics (headings, bold, lists, tables).
        // cleanContent function ensures we strip external styling/colors to match our theme ("Uniform Paste").
        if (html) {
            const cleanedHtml = cleanContent(html);
            document.execCommand('insertHTML', false, cleanedHtml);
        } else {
            // Insert plain text - browser handles wrapping in current block
            document.execCommand('insertText', false, text);
        }
    };

    // Smart Overlay Logic
    const handleMouseMove = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const pre = target.closest('pre');

        if (pre && contentRef.current?.contains(pre)) {
            const rect = pre.getBoundingClientRect();
            const parentRect = contentRef.current.getBoundingClientRect();

            // Calculate relative position
            setHoveredBlock({
                rect: {
                    ...rect,
                    top: rect.top - parentRect.top,
                    left: rect.left - parentRect.left,
                    width: rect.width,
                    height: rect.height
                } as DOMRect,
                text: pre.innerText
            });
        } else {
            // Only clear if we are significantly outside to prevent flickering
            // But for now, simple clear on non-pre
            if (hoveredBlock && !target.closest('button')) {
                setHoveredBlock(null);
            }
        }
    };

    const handleCopyBlock = async () => {
        if (!hoveredBlock) return;
        await navigator.clipboard.writeText(hoveredBlock.text);
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
    };

    return (
        <div className={`flex flex-col h-full rounded-xl border overflow-hidden transition-all ${isFocused
            ? 'border-cc-accent ring-1 ring-cc-accent'
            : 'border-cc-border'
            } ${theme === 'dark' ? 'dark' : ''}`}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-cc-surface border-b border-cc-border sticky top-0 z-10">
                <ToolbarButton icon={Bold} command="bold" label="Bold" />
                <ToolbarButton icon={Italic} command="italic" label="Italic" />

                {!hideHeadings && (
                    <>
                        <div className="w-px h-4 bg-cc-border mx-1" />
                        <ToolbarButton icon={Heading1} command="formatBlock" value="H1" label="Heading 1" />
                        <ToolbarButton icon={Heading2} command="formatBlock" value="H2" label="Heading 2" />
                    </>
                )}

                <div className="w-px h-4 bg-cc-border mx-1" />

                <select
                    onChange={(e) => exec('fontSize', e.target.value)}
                    className="h-7 text-xs bg-transparent border border-cc-border rounded px-1 text-cc-text outline-none"
                    title="Font Size"
                    defaultValue="2"
                >
                    <option value="1">10 pt</option>
                    <option value="2">12 pt (Default)</option>
                    <option value="3">14 pt</option>
                    <option value="4">18 pt</option>
                    <option value="5">24 pt</option>
                    <option value="6">36 pt</option>
                    <option value="7">48 pt</option>
                </select>

                <div className="relative flex items-center justify-center w-7 h-7 hover:bg-cc-tile-hover rounded cursor-pointer group" title="Text Color">
                    <Type size={16} className="text-cc-text-muted" />
                    <input
                        type="color"
                        onChange={(e) => exec('foreColor', e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    <div className="absolute bottom-1 w-4 h-1 bg-gradient-to-r from-red-500 via-green-500 to-blue-500 rounded-full" />
                </div>

                <div className="w-px h-4 bg-cc-border mx-1" />

                <ToolbarButton icon={List} command="insertUnorderedList" label="Bullet List" />
                <ToolbarButton icon={ListOrdered} command="insertOrderedList" label="Numbered List" />

                <div className="w-px h-4 bg-cc-border mx-1" />

                <ToolbarButton icon={AlignLeft} command="justifyLeft" label="Align Left" />
                <ToolbarButton icon={AlignCenter} command="justifyCenter" label="Align Center" />
            </div>

            {/* Editor Area */}
            <div
                className={`flex-1 relative bg-cc-elevated cursor-text overflow-y-auto`}
                onClick={() => contentRef.current?.focus()}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHoveredBlock(null)}
            >
                {/* Copy Overlay */}
                {hoveredBlock && (
                    <div
                        className={`absolute z-20 pointer-events-none ${theme === 'dark' ? 'dark' : ''}`}
                        style={{
                            top: hoveredBlock.rect.top,
                            left: hoveredBlock.rect.left,
                            width: hoveredBlock.rect.width,
                            height: hoveredBlock.rect.height
                        }}
                    >
                        <button
                            onClick={(e) => { e.stopPropagation(); handleCopyBlock(); }}
                            className={`absolute top-2 right-2 p-1.5 rounded-md shadow-sm border transition-all pointer-events-auto flex items-center gap-1.5 text-xs font-medium ${theme === 'dark'
                                ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white'
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            {copyFeedback ? (
                                <><span className="text-green-500">✓</span> Copied</>
                            ) : (
                                <>Copy</>
                            )}
                        </button>
                    </div>
                )}

                {!initialContent && !isFocused && placeholder && (
                    <div className="absolute top-4 left-4 text-cc-text-muted pointer-events-none select-none">
                        {placeholder}
                    </div>
                )}
                <style>{`
                    /* Premium List Styling */
                    .rte-content ul {
                        list-style-type: disc; 
                        padding-left: 1.5em; 
                        margin: 1em 0; 
                    }
                    .rte-content ol {
                        list-style-type: decimal; 
                        padding-left: 1.5em; 
                        margin: 1em 0; 
                    }
                    .rte-content li {
                        margin-bottom: 0.5em; /* Increased breathing room */
                        padding-left: 0.25em;
                        line-height: 1.6;
                        position: relative;
                    }
                    /* Nested lists */
                    .rte-content li > ul, .rte-content li > ol {
                        margin-top: 0.5em;
                        margin-bottom: 0.5em;
                    }
                    /* Accent-colored markers for visual flair */
                    .rte-content ul > li::marker {
                        color: var(--cc-accent);
                    }
                    .rte-content ol > li::marker {
                        color: var(--cc-accent);
                        font-weight: 600;
                    }

                    /* Typography Hierarchy (Standard Conversions) */
                    .rte-content h1 { 
                        font-size: 1.5em; 
                        font-weight: 800; 
                        margin-top: 1em; 
                        margin-bottom: 0.5em; 
                        color: var(--cc-text); 
                        line-height: 1.2; 
                        letter-spacing: -0.02em; 
                    }
                    .rte-content h2 { 
                        font-size: 1.25em; 
                        font-weight: 700; 
                        margin-top: 1em; 
                        margin-bottom: 0.4em; 
                        color: var(--cc-text); 
                        line-height: 1.3; 
                    }
                    .rte-content h3 { 
                        font-size: 1.1em; 
                        font-weight: 600; 
                        margin-top: 0.8em; 
                        margin-bottom: 0.3em; 
                        color: var(--cc-accent); 
                    }
                    .rte-content p { 
                        margin-bottom: 0.8em; 
                        line-height: 1.6; 
                    }

                    /* Code Styling */
                    /* Antigravity Code Blocks - Synced with CodeBlock.tsx */
                    .rte-content pre {
                        background: var(--is-dark, #1e1e1e);
                        color: #e2e8f0;
                        padding: 1.5rem;
                        border-radius: 0.75rem;
                        margin: 1.5rem 0;
                        overflow-x: auto;
                        font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
                        font-size: 0.875rem;
                        line-height: 1.7;
                        border: 1px solid rgba(148, 163, 184, 0.1);
                        /* Deep Dark Mode Border */
                        border-color: var(--is-dark, #333333); 
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
                        white-space: pre; /* Revert to pre to respect formatting */
                        tab-size: 2;
                        font-variant-ligatures: none;
                        -webkit-font-smoothing: antialiased;
                    }
                    
                    .dark .rte-content pre {
                         background: #1e1e1e !important;
                         border: 1px solid #333333 !important;
                         color: #d4d4d4 !important; /* VS Code Default Text */
                         box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);
                    }

                    /* Light Mode Override for Pre */
                    :not(.dark) .rte-content pre {
                        background: #f8fafc;
                        color: #1e293b;
                        border-color: #e2e8f0;
                        border-left-color: var(--cc-accent); /* Keep accent left only for light */
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                         border-left-width: 4px;
                         border-top-width: 1px;
                         border-right-width: 1px;
                         border-bottom-width: 1px;
                    }

                    .rte-content code {
                        font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
                        background: rgba(148, 163, 184, 0.2);
                        padding: 0.2em 0.4em;
                        border-radius: 4px;
                        font-size: 0.85em;
                        color: var(--cc-accent);
                        font-weight: 600;
                    }
                    
                    .dark .rte-content code {
                        color: #9cdcfe; /* Light Blue for inline code in dark */
                        background: #2d2d2d;
                    }

                    :not(.dark) .rte-content code {
                        background: rgba(148, 163, 184, 0.15);
                        color: #0f172a;
                    }

                    .rte-content pre code {
                        background: transparent;
                        padding: 0;
                        font-size: 1em;
                        color: inherit;
                        font-weight: 400;
                        box-shadow: none;
                        border: none;
                        background-color: transparent !important; /* Force transparent in pre */
                    }
                    
                    .dark .rte-content pre code {
                         color: inherit;
                    }

                    /* Horizontal Rule */
                    .rte-content hr {
                        border: 0;
                        border-top: 2px solid var(--cc-border);
                        margin: 2em 0;
                    }

                    /* High Contrast, Auto-Width Table */
                    /* Force Auto-Width to prevent stretching */
                    .rte-content table {
                        width: auto !important;
                        display: inline-table !important;
                        min-width: 200px;
                        max-width: 100% !important;
                    border-collapse: collapse;
                    margin: 1em 0;
                    border: 2px solid var(--cc-border);
                    border-radius: 4px;
                    font-size: 0.9em;
                    display: inline-table;
                    }

                    /* Default (Light Mode) Headers */
                    .rte-content th {
                        background - color: var(--cc-tile-hover);
                    font-weight: 800;
                    padding: 8px 12px;
                    text-align: left;
                    border-bottom: 2px solid var(--cc-border);
                    border-right: 1px solid var(--cc-border-muted);
                    color: var(--cc-text);
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    }

                    .rte-content td {
                        padding: 6px 12px;
                    border-bottom: 1px solid var(--cc-border-muted);
                    border-right: 1px solid var(--cc-border-muted);
                    color: var(--cc-text);
                    background-color: transparent;
                    }

                    .rte-content th:last-child, .rte-content td:last-child {border - right: none; }

                    /* Semitones (Zebra Striping) - Premium Contrast */
                    .rte-content tr:nth-child(odd) {
                        background-color: var(--cc-surface);
                    }
                    .rte-content tr:nth-child(even) { 
                        background-color: var(--cc-elevated);
                    }
                    .rte-content tr:hover {
                        background-color: var(--cc-tile-hover); /* Interactive feel */
                    }
                    
                    /* High Contrast, Auto-Width Table */
                    /* Light Mode Headers */
                    .rte-content th { 
                        background-color: var(--cc-tile); 
                        font-weight: 700; 
                        padding: 10px 14px; 
                        text-align: left; 
                        border-bottom: 2px solid var(--cc-border); 
                        border-right: 1px solid var(--cc-border-muted); 
                        color: var(--cc-text); 
                        text-transform: uppercase;
                        font-size: 0.75rem;
                        letter-spacing: 0.05em;
                    }

                    /* Dark Mode Specifics (handled by vars mostly, but explicit tweaks for contrast) */
                    .dark .rte-content th { 
                        background-color: rgba(255, 255, 255, 0.03);
                        border-bottom-color: var(--cc-border);
                    }
                    .dark .rte-content tr:nth-child(even) { 
                        background-color: rgba(255, 255, 255, 0.02);
                    }

                    /* Premium Links */
                    .rte-content a {
                        color: var(--cc-accent);
                        text-decoration: none;
                        border-bottom: 1px solid transparent;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    .rte-content a:hover {
                        border-bottom-color: var(--cc-accent);
                        filter: brightness(1.1);
                    }

                    /* Elegant Blockquotes */
                    .rte-content blockquote {
                        border-left: 4px solid var(--cc-accent);
                        background: var(--cc-surface);
                        padding: 1em 1.5em;
                        margin: 1.5em 0;
                        border-radius: 0 12px 12px 0;
                        font-style: italic;
                        color: var(--cc-text-muted);
                        position: relative;
                        overflow: hidden;
                    }
                    /* Subtle quote mark decoration */
                    .rte-content blockquote::before {
                        content: '"';
                        position: absolute;
                        top: -0.2em;
                        right: 0.2em;
                        font-size: 4em;
                        line-height: 1;
                        color: var(--cc-accent);
                        opacity: 0.1;
                        font-family: serif;
                        pointer-events: none;
                    }

                    /* Media (Images) */
                    .rte-content img {
                        border-radius: 12px;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                        margin: 1.5em auto;
                        display: block;
                        max-width: 100%;
                        transition: transform 0.2s;
                    }
                    .rte-content img:hover {
                        transform: scale(1.01);
                    }

                    /* Selection Styling */
                    .rte-content ::selection {
                        background: var(--cc-accent-muted); /* Softer highlight */
                        color: var(--cc-text);
                    }
                    .rte-content img::selection {
                        background: transparent;
                        filter: brightness(0.8);
                    }
                `}</style>
                <div
                    ref={contentRef}
                    contentEditable
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="rte-content w-full h-full p-4 outline-none prose dark:prose-invert max-w-none text-sm leading-relaxed text-cc-text"
                    style={{ minHeight: '150px' }}
                />
            </div>
        </div>
    );
};